import { useState, useEffect } from "react";
import {
  ArrowLeft,
  CalendarIcon,
  CheckCircle2,
  AlertCircle,
  FileImage,
  Clipboard,
  Eye,
  Download,
  FileText,
} from "lucide-react";
import MainLayout from "../../../components/layout/MainLayout";
import { getObservation } from "../../../../Services/observationService";
import { useParams, Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function ObservationDetails() {
  const navigate = useNavigate();
  const { observationId } = useParams();
  const [observation, setObservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    const fetchObservation = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getObservation(observationId);
        if (!response || !response.data) {
          throw new Error("No data received from the server");
        }

        const observationDetails = response.data;
        setObservation(observationDetails);
      } catch (error) {
        console.error("Error fetching observation:", error);
        setError(error.message || "Failed to fetch observation details");
      } finally {
        setLoading(false);
      }
    };

    if (observationId) {
      fetchObservation();
    } else {
      setError("No observation ID provided");
      setLoading(false);
    }
  }, [observationId]);

  const getRiskLevelBadge = (level) => {
    if (!level)
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Not Specified
        </span>
      );

    switch (level.toLowerCase()) {
      case "low":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Low Risk
          </span>
        );
      case "medium":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Medium Risk
          </span>
        );
      case "high":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            High Risk
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Not Specified
          </span>
        );
    }
  };

  const getCategoryBadge = (category) => {
    if (!category)
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Not Specified
        </span>
      );

    const categories = {
      safety: { color: "bg-red-100 text-red-800", label: "Safety" },
      quality: { color: "bg-blue-100 text-blue-800", label: "Quality" },
      environmental: {
        color: "bg-green-100 text-green-800",
        label: "Environmental",
      },
      efficiency: {
        color: "bg-purple-100 text-purple-800",
        label: "Efficiency",
      },
      equipment: { color: "bg-orange-100 text-orange-800", label: "Equipment" },
      positive: { color: "bg-emerald-100 text-emerald-800", label: "Positive" },
      other: { color: "bg-gray-100 text-gray-800", label: "Other" },
    };

    const categoryInfo = categories[category.toLowerCase()] || categories.other;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryInfo.color}`}
      >
        {categoryInfo.label}
      </span>
    );
  };

  const getRiskLevelText = (level) => {
    if (!level) return "Not Specified";

    switch (level.toLowerCase()) {
      case "low":
        return "Low Risk";
      case "medium":
        return "Medium Risk";
      case "high":
        return "High Risk";
      default:
        return "Not Specified";
    }
  };

  const getCategoryText = (category) => {
    if (!category) return "Not Specified";

    const categories = {
      safety: "Safety",
      quality: "Quality",
      environmental: "Environmental",
      efficiency: "Efficiency",
      equipment: "Equipment",
      positive: "Positive",
      other: "Other",
    };

    return categories[category.toLowerCase()] || "Other";
  };

  const generatePDF = async () => {
    try {
      setGeneratingPdf(true);

      const observationId_display =
        observation?._id?.$oid?.substring(0, 8) || "Unknown";
      const projectNumber = observation?.projectNumber || "Not specified";
      const observerName =
        observation?.yourNames?.yourName || "Unknown Observer";
      const observerDate =
        observation?.observerDetails?.observerDate || "Not specified";
      const status = observation?.status || "Unknown";
      const createdAt = observation?.createdAt
        ? new Date(observation.createdAt).toLocaleDateString()
        : "Unknown date";
      const observationTitle =
        observation?.observerDetails?.observerDetail || "Observation Details";
      const observationCategory =
        observation?.observationDetails?.observationCategory;
      const riskLevel = observation?.riskAssessment?.riskLevel;
      const description =
        observation?.observationDetails?.observationDescription ||
        "No description provided";

      const actionTaken = observation?.observationAction?.takeAction;
      const hasTakenAction = actionTaken?.isTakeAction === true;
      const actionTakenText = actionTaken?.value || "Not specified";

      const furtherAction = observation?.observationAction?.furtherAction;
      const needsFurtherAction = furtherAction?.isFurtherAction === true;
      const furtherActionText = furtherAction?.value || "Not specified";

      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.setTextColor(44, 62, 80);
      doc.text("Observation Report", 105, 15, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Observation ID: ${observationId_display}`, 105, 22, {
        align: "center",
      });

      doc.setDrawColor(200, 200, 200);
      doc.line(20, 25, 190, 25);

      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text("Project Information", 20, 35);

      doc.autoTable({
        startY: 40,
        head: [["Field", "Value"]],
        body: [
          ["Project Number", projectNumber],
          ["Observer", observerName],
          ["Date Observed", observerDate],
          ["Status", status],
          ["Created", createdAt],
        ],
        theme: "grid",
        headStyles: {
          fillColor: [66, 133, 244],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { left: 20, right: 20 },
      });

      let currentY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text("Observation Details", 20, currentY);

      currentY += 5;

      doc.autoTable({
        startY: currentY,
        head: [["Field", "Value"]],
        body: [
          ["Title", observationTitle],
          ["Category", getCategoryText(observationCategory)],
          ["Risk Level", getRiskLevelText(riskLevel)],
          ["Description", description],
        ],
        theme: "grid",
        headStyles: {
          fillColor: [66, 133, 244],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { left: 20, right: 20 },
        columnStyles: {
          1: { cellWidth: "auto", minCellWidth: 85 },
        },
      });

      currentY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text("Actions", 20, currentY);

      currentY += 5;

      doc.autoTable({
        startY: currentY,
        head: [["Type", "Details"]],
        body: [
          [
            "Actions Taken",
            hasTakenAction ? actionTakenText : "No immediate action was taken",
          ],
          [
            "Further Actions Required",
            needsFurtherAction
              ? furtherActionText
              : "No further action is required",
          ],
        ],
        theme: "grid",
        headStyles: {
          fillColor: [66, 133, 244],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { left: 20, right: 20 },
        columnStyles: {
          1: { cellWidth: "auto", minCellWidth: 85 },
        },
      });

      const now = new Date();
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated on ${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`,
        105,
        285,
        { align: "center" }
      );

      doc.save(`Observation_${observationId_display}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <MainLayout title="Loading Observation...">
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-2">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-gray-500">Loading observation details...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout title="Error">
        <div className="flex flex-col items-center justify-center min-h-screen px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
              <h2 className="text-lg font-semibold text-red-700">Error</h2>
            </div>
            <p className="text-gray-700 mb-4">{error}</p>
            <Link
              to="/observations"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Observations
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!observation) {
    return (
      <MainLayout title="No Data">
        <div className="flex flex-col items-center justify-center min-h-screen px-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-500 mr-2" />
              <h2 className="text-lg font-semibold text-yellow-700">
                No Data Found
              </h2>
            </div>
            <p className="text-gray-700 mb-4">
              No observation data was found for the provided ID.
            </p>
            <Link
              to="/observations"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Observations
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  const observationId_display =
    observation?._id?.$oid?.substring(0, 8) || "Unknown";
  const projectNumber = observation?.projectNumber || "Not specified";
  const observerName = observation?.yourNames?.yourName || "Unknown Observer";
  const observerInitial = observerName ? observerName.charAt(0) : "?";
  const observerDate =
    observation?.observerDetails?.observerDate || "Not specified";
  const status = observation?.status || "Unknown";
  const createdAt = observation?.createdAt
    ? new Date(observation.createdAt).toLocaleDateString()
    : "Unknown date";
  const observationTitle =
    observation?.observerDetails?.observerDetail || "Observation Details";
  const observationCategory =
    observation?.observationDetails?.observationCategory;
  const riskLevel = observation?.riskAssessment?.riskLevel;
  const description =
    observation?.observationDetails?.observationDescription ||
    "No description provided";

  const actionTaken = observation?.observationAction?.takeAction;
  const hasTakenAction = actionTaken?.isTakeAction === true;
  const actionTakenText = actionTaken?.value || "Not specified";

  const furtherAction = observation?.observationAction?.furtherAction;
  const needsFurtherAction = furtherAction?.isFurtherAction === true;
  const furtherActionText = furtherAction?.value || "Not specified";

  const hasMedia = !!observation?.observationMedia;
  const mediaUrl = observation?.observationMedia || "/placeholder.svg";

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <MainLayout title="Observation Details">
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Link className="p-2 mr-2 rounded-full hover:bg-gray-100 transition-colors">
              <ArrowLeft onClick={() => handleGoBack()} className="h-5 w-5" />
            </Link>
            <h1 className="text-3xl font-bold">Observation Details</h1>
          </div>

          {/* PDF Download Button */}
          <button
            onClick={generatePDF}
            disabled={generatingPdf}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generatingPdf ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Generating PDF...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Download PDF
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Project Info */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Project Information
              </h2>
            </div>
            <div className="px-6 py-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Project Number
                  </h3>
                  <p className="text-lg font-semibold">{projectNumber}</p>
                </div>

                <hr className="border-gray-200" />

                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Observer
                  </h3>
                  <div className="flex items-center mt-1">
                    <div className="h-8 w-8 mr-2 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                      {observerInitial}
                    </div>
                    <span className="font-medium">{observerName}</span>
                  </div>
                </div>

                <hr className="border-gray-200" />

                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Date Observed
                  </h3>
                  <div className="flex items-center mt-1">
                    <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{observerDate}</span>
                  </div>
                </div>

                <hr className="border-gray-200" />

                <div>
                  <h3 className="text-sm font-medium text-gray-500">Status</h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1 capitalize">
                    {status}
                  </span>
                </div>

                <hr className="border-gray-200" />

                <div>
                  <h3 className="text-sm font-medium text-gray-500">Created</h3>
                  <div className="flex items-center mt-1">
                    <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{createdAt}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Observation Details */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {observationTitle}
                </h2>
                <div className="flex gap-2">
                  {getCategoryBadge(observationCategory)}
                  {getRiskLevelBadge(riskLevel)}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Observation #{observationId_display}
              </p>
            </div>

            <div className="px-6 py-6">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab("details")}
                    className={`${
                      activeTab === "details"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Details
                  </button>
                  <button
                    onClick={() => setActiveTab("actions")}
                    className={`${
                      activeTab === "actions"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Actions
                  </button>
                  <button
                    onClick={() => setActiveTab("media")}
                    className={`${
                      activeTab === "media"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Media
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="mt-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Description
                      </h3>
                      <p className="text-gray-700">{description}</p>
                    </div>

                    <hr className="border-gray-200" />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Risk Assessment
                      </h3>
                      <div className="flex items-center">
                        {riskLevel === "high" ? (
                          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                        ) : riskLevel === "medium" ? (
                          <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                        ) : (
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                        )}
                        <span className="capitalize">
                          {riskLevel || "Not specified"} Risk Level
                        </span>
                      </div>
                      {observation?.riskAssessment?.value && (
                        <p className="mt-2 text-gray-700">
                          {observation.riskAssessment.value}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions Tab */}
                {activeTab === "actions" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Actions Taken
                      </h3>
                      {hasTakenAction ? (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                          <div className="flex items-start">
                            <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                            <p className="text-gray-700">{actionTakenText}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">
                          No immediate action was taken
                        </p>
                      )}
                    </div>

                    <hr className="border-gray-200" />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Further Actions Required
                      </h3>
                      {needsFurtherAction ? (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                          <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                            <p className="text-gray-700">{furtherActionText}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">
                          No further action is required
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Media Tab */}
                {activeTab === "media" && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Media Attachments
                    </h3>
                    {hasMedia ? (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="relative aspect-video">
                          <img
                            src={mediaUrl}
                            alt="Observation media"
                            className="object-cover w-full h-full"
                          />
                        </div>
                        <div className="p-3 bg-gray-50 flex justify-between items-center">
                          <span className="text-sm text-gray-500">
                            Attachment
                          </span>
                          <a
                            href={mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Full Size
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-8 border border-dashed rounded-lg">
                        <FileImage className="h-12 w-12 text-gray-400 mb-2" />
                        <p className="text-gray-500">No media attachments</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
